import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { SimLookupInjection } from "features/simStatusLookup/di/SimLookupInjection";
import { SimLookupRequestEntity } from "features/simStatusLookup/domain/entities/SimLookupRequestEntity";
import { SimType } from "features/simStatusLookup/domain/entities/SimLookupResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";
import { SimStatusLookupConstants } from "../constants/SimStatusLookupConstants";
import { ISimLookupObservedState } from "../interfaces/ISimLookupState";

// ─── Input ────────────────────────────────────────────────────────────────────

class SimLookupInput implements Disposable {
  public fetchSupportFeature = new BehaviorSubject<SimLookupRequestEntity>(
    undefined,
  );
  public clearLookupData = new BehaviorSubject<void>(undefined);

  [Symbol.dispose](): void {
    this.fetchSupportFeature.complete();
    this.clearLookupData.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

class SimLookupOutput implements Disposable {
  public onError = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.onError.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class SimLookupViewModel extends BasedObservedViewModel<
  SimLookupInput,
  SimLookupOutput,
  ISimLookupObservedState
> {
  constructor() {
    super(new SimLookupInput(), new SimLookupOutput());
  }

  private useCase = SimLookupInjection.getUseCase();

  protected override createObservedState(): ISimLookupObservedState {
    return {
      loading: false,
      lookupData: {},
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchSupportFeature, async (params) => {
      try {
        this.setObservedState({
          loading: true,
          lookupData: {},
        });
        const res = await this.useCase.getSimLookup(params);
        if (!res) return;

        if (
          res.data &&
          (res.data.simType === SimType.INBOUND ||
            res.data.simType === SimType.OUTBOUND)
        ) {
          this.setObservedState({ lookupData: res.data });
          return;
        }

        this.output.onError.next(
          strings.SupportFeature.SimLookup.Output.isNotExist,
        );
      } catch (error) {
        console.error(error);
        this.output.onError.next(
          error?.message ||
            SimStatusLookupConstants.getErrorMessage(error?.code),
        );
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    this.skipFirstInitial(this.input.clearLookupData, () => {
      this.setObservedState({ lookupData: {} });
    });
  }
}
